import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoDesk } from "@/lib/floorReturn";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { AuthImg } from "@/components/AuthImg";
import { Replace, RotateCcw, Loader2, AlertTriangle, History, EyeOff, ImagePlus, ArrowRight } from "lucide-react";

// ONE ACTION, EVERY SURFACE — the scanned truck card, the scanned part card, the photo viewer,
// the desk galleries and the Rapid Capture strip all mount THIS. His words: "not three versions
// that drift."
//
//   · The reason is a tap-list he owns (Vehicle Setup), not free text — countable.
//   · "Wrong truck" / "wrong part" carry an action, so the reason LEADS TO THE MOVE rather than
//     to an archive: "archiving a good photo that was simply on the wrong truck loses it."
//   · "Wrong part" on a TRUCK photo MOVES it onto the part it shows (his ruling, 28 Oct — off the
//     truck, onto the part), with "attach a copy instead" one tap away for a photo the truck should
//     keep. Part → truck stays REFUSED WITH A DOOR, not a sentence.
//   · There is a way out for "it's wrong but I don't know where it belongs": that, and only that,
//     is when archive is the right answer to "wrong truck".
//   · The warning says WHAT WILL HAPPEN ("the part will have no photo afterwards"), never just
//     "are you sure" — and it is louder when a customer is looking at that part right now.
//   · Every correction is soft, logged with who and why, and undoable in one press by anyone who
//     could make it in the first place. Cancel writes nothing at all.
const LEVEL = {
  critical: "border-[#B01E28] bg-red-50 text-[#B01E28]",
  high: "border-amber-400 bg-amber-50 text-amber-900",
};

// A move changes TWO records, and the strip that reports it lives on a third component, so the
// surfaces tell each other rather than each polling. Cheap, and nothing goes stale.
let TICK = 0;
const SUBS = new Set();
function notifyPhotoFixed() { TICK += 1; SUBS.forEach((f) => f(TICK)); }
export function usePhotoFixTick() {
  const [t, setT] = useState(TICK);
  useEffect(() => { const f = (v) => setT(v); SUBS.add(f); return () => { SUBS.delete(f); }; }, []);
  return t;
}

export const recordPath = (scope, id) => (scope === "donor" ? `/donors/${id}` : `/parts/${id}`);

function person(email) {
  const local = String(email || "").split("@")[0];
  if (!local) return "somebody";
  return local.split(/[._-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function day(at) {
  if (!at) return "";
  const d = new Date(at);
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function PhotoFixAction({ scope, recordId, photo, onDone, compact = false, big = false,
                                 label = "Wrong photo?", testid = "photo-fix" }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [dontKnow, setDontKnow] = useState(false);
  const [busy, setBusy] = useState(false);

  const canFix = has("archive_photos");
  const archived = !!photo?.is_archived;
  const moveAction = scope === "donor" ? "move_truck" : "move_part";
  const toPart = scope === "donor" && reason?.action === "move_part";
  const moveWord = scope === "donor" && !toPart ? "truck" : "part";
  const crossMove = !!reason?.action && reason.action !== moveAction && !toPart;
  const isMove = (reason?.action === moveAction || toPart) && !dontKnow;

  useEffect(() => {
    if (!open || reasons.length) return;
    api.get("/photo-reasons").then(({ data }) => setReasons(data.items || [])).catch(() => {});
  }, [open, reasons.length]);

  if (!canFix || !photo?.file_id) return null;

  const reset = () => { setReason(null); setNote(""); setTarget(""); setPreview(null); setDontKnow(false); setBusy(false); };
  const shut = (refresh) => { setOpen(false); reset(); if (refresh) { notifyPhotoFixed(); onDone?.(); } };
  const go = (path) => { setOpen(false); reset(); (pathname.startsWith("/floor") ? goDesk : navigate)(path); };

  const restore = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const { data } = await api.post("/photo-fix/restore", { scope, record_id: recordId, file_id: photo.file_id });
      scanAccept(data.message); toast.success(data.message); notifyPhotoFixed(); onDone?.();
    } catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const bodyFor = (confirm, moving) => ({
    scope, record_id: recordId, file_id: photo.file_id,
    reason_code: reason?.code || "", note, confirm,
    ...(moving ? { to_scan: target.trim(), ...(toPart ? { to_scope: "part" } : {}) } : {}),
  });

  const call = async (confirm, moving) => {
    const { data } = await api.post(moving ? "/photo-fix/move" : "/photo-fix/archive", bodyFor(confirm, moving));
    return data;
  };

  const ask = async (moving = isMove) => {
    setBusy(true);
    try { setPreview(await call(false, moving)); }
    catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // "I don't know where it belongs" — out of the wrong place, reason recorded, findable later.
  const takeItOff = async () => { setDontKnow(true); setPreview(null); await ask(false); };

  const commit = async () => {
    setBusy(true);
    try {
      const data = await call(true, isMove);
      scanAccept(data.message);
      toast.success(data.message, {
        duration: 12000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const r = await api.post(`/photo-fix/${data.correction_id}/undo`);
              toast.success(r.data.message); notifyPhotoFixed(); onDone?.();
            } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
          },
        },
      });
      shut(true);
    } catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (archived) {
    return (
      <button type="button" data-testid={`${testid}-restore`} onClick={restore} disabled={busy}
        title="Put this photo back"
        className={compact
          ? "inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all"
          : big
            ? "inline-flex items-center gap-2 rounded-xl border-2 border-emerald-400 bg-white px-4 h-12 text-base font-bold text-emerald-700 active:bg-emerald-50"
            : "inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 h-9 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"}>
        <RotateCcw className={big ? "w-5 h-5" : "w-3.5 h-3.5"} />{compact ? "" : "Put it back"}
      </button>
    );
  }

  return (
    <>
      <button type="button" data-testid={`${testid}-open`}
        onClick={(e) => { e.stopPropagation(); reset(); setOpen(true); }} title={label}
        className={compact
          ? "inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 hover:bg-[#B01E28] hover:text-white hover:border-[#B01E28] transition-all"
          : big
            ? "inline-flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 h-12 text-base font-bold text-slate-700 active:border-[#B01E28] active:text-[#B01E28]"
            : "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 h-9 text-xs font-semibold text-slate-700 hover:border-[#B01E28] hover:text-[#B01E28]"}>
        <Replace className={big ? "w-5 h-5" : "w-3.5 h-3.5"} />{compact ? "" : label}
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) shut(false); }}>
        <DialogContent className="rounded-xl max-w-lg max-h-kb overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-title`}>What's wrong with this photo?</DialogTitle>
            <DialogDescription>
              Nothing is deleted. Whatever you do here is logged with your name and can be put back
              in one press — and backing out now leaves the photo exactly where it is.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-1.5">
              {reasons.map((r) => (
                <button key={r.code} type="button" data-testid={`${testid}-reason-${r.code.replace(/[^a-z]+/g, "-")}`}
                  onClick={() => { setReason(r); setPreview(null); setDontKnow(false); }}
                  className={`text-left rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                    reason?.code === r.code ? "border-[#E0781A] bg-orange-50 text-[#C76714]" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
                  {r.label}
                  {r.action === moveAction && <span className="block text-[11px] font-normal text-slate-500">then it moves to the right {scope === "donor" ? "truck" : "part"} — the photo is kept</span>}
                  {scope === "donor" && r.action === "move_part" && <span className="block text-[11px] font-normal text-slate-500">it's a photo of a part — it moves off the truck onto that part</span>}
                  {r.reserved && <span className="block text-[11px] font-normal text-slate-500">needs a note, so he can add it to the list properly</span>}
                </button>
              ))}
            </div>

            <Textarea data-testid={`${testid}-note`} value={note} rows={2}
              onChange={(e) => { setNote(e.target.value); setPreview(null); }}
              placeholder={reason?.reserved ? "Say what's wrong (required for Other)" : "Anything worth adding (optional)"}
              className="rounded-lg text-sm" />

            {/* CROSS-MOVE: refused, and the refusal is a DOOR into the tool that does do it. */}
            {crossMove && !preview && (
              <div data-testid={`${testid}-cross`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-800">
                  {scope === "donor"
                    ? "This is the truck's own photo of how it arrived. Moving it onto a part would strip the truck of that — put a COPY on the part instead, and both keep it."
                    : "A part photo is a photo of that part. It doesn't belong on a truck."}
                </p>
                {scope === "donor" ? (
                  <Button data-testid={`${testid}-cross-attach`} onClick={() => go(`${recordPath("donor", recordId)}?attach=${photo.file_id}`)}
                    className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11 w-full">
                    <ImagePlus className="w-4 h-4 mr-1.5" /> Open “Attach to part”
                  </Button>
                ) : (
                  <Button data-testid={`${testid}-cross-switch`}
                    onClick={() => { const r = reasons.find((x) => x.action === moveAction); if (r) { setReason(r); setPreview(null); } }}
                    disabled={!reasons.some((x) => x.action === moveAction)}
                    className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11 w-full">
                    <ArrowRight className="w-4 h-4 mr-1.5" /> Move it to the right part instead
                  </Button>
                )}
                <button type="button" data-testid={`${testid}-cross-archive`} onClick={takeItOff} disabled={busy}
                  className="text-xs font-semibold text-slate-600 underline">
                  Or just take it off this {moveWord} for now
                </button>
              </div>
            )}

            {isMove && !crossMove && (
              <div className="rounded-lg border-2 border-[#E0781A]/40 bg-orange-50 p-2.5">
                <p className="text-xs font-bold text-[#C76714] mb-1.5">Scan the {moveWord} it SHOULD be on</p>
                <ScanField testid={`${testid}-scan`} onScan={(v) => { setTarget(v); setPreview(null); }}
                  submitLabel="Use this" placeholder={moveWord === "truck" ? "Scan the truck label or type its stock number…" : toPart ? "Scan the tag on the part this photo shows…" : "Scan the tag on the right part…"}
                  uppercase autoFocus={false} />
                {target && <p data-testid={`${testid}-target`} className="text-xs text-slate-700 mt-1.5">Moving it to <b className="font-mono">{target}</b></p>}
                <button type="button" data-testid={`${testid}-dont-know`} onClick={takeItOff} disabled={busy}
                  className="mt-2 text-xs font-semibold text-slate-600 underline">
                  I don't know which {moveWord} it belongs to — take it off for now
                </button>
                {toPart && (
                  <button type="button" data-testid={`${testid}-keep-copy`} onClick={() => go(`${recordPath("donor", recordId)}?attach=${photo.file_id}`)}
                    className="mt-1 block text-xs font-semibold text-slate-600 underline">
                    The truck should keep it too — attach a copy to the part instead
                  </button>
                )}
              </div>
            )}

            {preview && (
              <div data-testid={`${testid}-confirm`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-800">{preview.prompt}</p>
                {(preview.warnings || []).map((w, i) => (
                  <p key={i} data-testid={`${testid}-warning-${i}`}
                    className={`text-xs font-semibold rounded-md border px-2 py-1.5 flex items-start gap-1.5 ${LEVEL[w.level] || LEVEL.high}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w.text}
                  </p>
                ))}
                <p className="text-[11px] text-slate-500">{preview.reversible}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            {preview ? (
              <Button data-testid={`${testid}-commit`} onClick={commit} disabled={busy}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {toPart && isMove ? "Yes — move it onto the part" : isMove ? `Yes — move it to the right ${moveWord}` : "Yes — take it off this record"}
              </Button>
            ) : !crossMove && (
              <Button data-testid={`${testid}-next`} onClick={() => ask()}
                disabled={busy || !reason || (isMove && !target.trim()) || (reason?.reserved && !note.trim())}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {!reason ? "Pick what's wrong" : isMove ? `Check the ${moveWord}` : "What happens if I do?"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// WHERE THE PHOTOS WENT — on the record they LEFT, and on the one they arrived on. A toast lasts
// seconds and a tablet in the yard gets looked away from; this line stays until the job is done,
// accumulates rather than overwrites, and the destination is one tap away if he wants to check it
// landed. Read from the correction log, so it survives a refresh.
export function PhotoMoveTrail({ scope, recordId, onDone, testid = "photo-move-trail" }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const { pathname } = useLocation();
  const tick = usePhotoFixTick();
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!recordId) return;
    try {
      const { data } = await api.get("/photo-fix/record-log", { params: { scope, record_id: recordId } });
      setLog(data);
    } catch { /* a missing trail must never break the card */ }
  }, [scope, recordId]);

  useEffect(() => { load(); }, [load, tick]);

  const out = log?.moved_out || [];
  const into = log?.moved_in || [];
  if (!out.length && !into.length) return null;

  const go = (id, sc = scope) => (pathname.startsWith("/floor") ? goDesk : navigate)(recordPath(sc, id));

  const groups = [];
  out.forEach((r) => {
    const g = groups.find((x) => x.id === r.other_id);
    if (g) g.n += 1; else groups.push({ id: r.other_id, scope: r.other_scope || scope, label: r.other_label, n: 1 });
  });
  const last = out[out.length - 1];

  const undoLast = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/photo-fix/${last.correction_id}/undo`);
      toast.success(data.message); notifyPhotoFixed(); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid} className="space-y-1.5">
      {!!out.length && (
        <div data-testid={`${testid}-out`}
          className="rounded-lg border-2 border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-700 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <b className="font-bold text-slate-900">{out.length} photo{out.length === 1 ? "" : "s"} moved</b>
          <span>off this {scope === "donor" ? "truck" : "part"} —</span>
          {groups.map((g, i) => (
            <span key={g.id} className="inline-flex items-center gap-1">
              {g.n} to
              <button type="button" data-testid={`${testid}-to-${i}`} onClick={() => go(g.id, g.scope)}
                className="font-mono font-bold text-[#0369a1] underline">{g.label}</button>
              {i < groups.length - 1 ? "," : ""}
            </span>
          ))}
          {has("archive_photos") && (
            <button type="button" data-testid={`${testid}-undo`} onClick={undoLast} disabled={busy}
              className="ml-auto inline-flex items-center gap-1 font-bold text-[#B01E28] underline">
              <RotateCcw className="w-3.5 h-3.5" />{out.length === 1 ? "Undo" : "Undo the last one"}
            </button>
          )}
        </div>
      )}
      {into.map((r, i) => (
        <div key={r.correction_id} data-testid={`${testid}-in-${i}`}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
          1 photo moved here from{" "}
          <button type="button" data-testid={`${testid}-from-${i}`} onClick={() => go(r.other_id, r.other_scope || scope)}
            className="font-mono font-bold text-[#0369a1] underline">{r.other_label}</button>
          {r.reason ? ` · ${r.reason.toLowerCase()}` : ""} · by {person(r.by)}{r.at ? ` · ${day(r.at)}` : ""}
        </div>
      ))}
    </div>
  );
}

// REMOVED PHOTOS, ON THE FLOOR. A Technician's screens are the floor cards — the desk pages
// redirect them straight back — so if the only "put it back" control lived on a desk gallery, the
// person who made the mistake could not fix it, which is the opposite of his ruling. Off by
// default ("removed photos shouldn't clutter the normal view"), and it names who and why.
export function RemovedPhotosStrip({ scope, recordId, photos, onDone, testid = "removed-photos" }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const gone = (photos || []).filter((p) => p?.file_id && p.is_archived);
  if (!has("archive_photos") || !gone.length) return null;
  return (
    <div data-testid={testid}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 underline">
        <EyeOff className="w-4 h-4" />{open ? "Hide" : "Show"} removed photo{gone.length === 1 ? "" : "s"} ({gone.length})
      </button>
      {open && (
        <div data-testid={`${testid}-list`} className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {gone.map((p, i) => (
            <div key={p.file_id}>
              <div className="relative">
                <AuthImg fileId={p.file_id} thumb alt={`removed photo ${i + 1}`}
                  className="w-full aspect-square object-cover rounded-xl border-2 border-slate-200 grayscale opacity-70" />
                <div className="absolute top-1 right-1">
                  <PhotoFixAction scope={scope} recordId={recordId} photo={p} compact
                    testid={`${testid}-${i}`} onDone={onDone} />
                </div>
              </div>
              <PhotoHistory photo={p} testid={`${testid}-history-${i}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// WHO TOOK IT OFF, WHY, AND WHEN — in the yard's words, not the database's: "Removed by Dave ·
// wrong truck · 25 Sep". And if a photo keeps bouncing, that shows too, because it means two
// people disagree about whether it belongs.
export function PhotoHistory({ photo, testid = "photo-history" }) {
  const rows = photo?.history || [];
  if (!rows.length) return null;
  return (
    <div data-testid={testid} className="mt-1 space-y-0.5">
      {rows.slice(-4).map((h, i) => {
        const what = String(h.what || "");
        return (
          <p key={i} className="text-[11px] text-slate-500 flex items-start gap-1">
            <History className="w-2.5 h-2.5 mt-1 shrink-0" />
            <span>
              <b className="text-slate-700">{what.charAt(0).toUpperCase() + what.slice(1)}</b> by {person(h.by)}
              {h.reason ? ` · ${h.reason.toLowerCase()}` : ""}
              {h.from ? ` · from ${h.from}` : ""}
              {h.note ? ` · “${h.note}”` : ""}
              {h.at ? ` · ${day(h.at)}` : ""}
            </span>
          </p>
        );
      })}
    </div>
  );
}
